import { useQuery } from "@tanstack/react-query";
import api from "@/services/api";
import type { ProductResponse, PaginatedResponse } from "@byk/shared";

interface ProductFilters {
  vendorId?: string;
  categoryId?: string;
  active?: boolean;
  featured?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
}

export function useProducts(filters: ProductFilters = {}) {
  const params = new URLSearchParams();

  if (filters.vendorId) params.set("vendorId", filters.vendorId);
  if (filters.categoryId) params.set("categoryId", filters.categoryId);
  if (filters.active !== undefined) params.set("active", String(filters.active));
  if (filters.featured !== undefined) params.set("featured", String(filters.featured));
  if (filters.search) params.set("search", filters.search);
  if (filters.limit) params.set("limit", String(filters.limit));
  if (filters.offset) params.set("offset", String(filters.offset));

  return useQuery({
    queryKey: ["products", filters],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<ProductResponse>>(
        `/api/products?${params.toString()}`
      );
      return response.data;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useProduct(id: string) {
  return useQuery({
    queryKey: ["products", id],
    queryFn: async () => {
      const response = await api.get<{ data: ProductResponse }>(`/api/products/${id}`);
      return response.data.data;
    },
    enabled: !!id,
    staleTime: 1000 * 60 * 5,
  });
}

export function useFeaturedProducts(limit = 6) {
  return useProducts({ featured: true, limit });
}

export default useProducts;
